import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faRobot, 
  faBrain, 
  faCheckCircle, 
  faLightbulb, 
  faExclamationTriangle,
  faChartLine,
  faUserClock
} from '@fortawesome/free-solid-svg-icons';
import styled, { keyframes } from 'styled-components';

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
`;

const spin = keyframes`
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
`;

const pulse = keyframes`
  0% { transform: scale(0.95); box-shadow: 0 0 0 0 rgba(106, 58, 143, 0.7); }
  70% { transform: scale(1); box-shadow: 0 0 0 10px rgba(106, 58, 143, 0); }
  100% { transform: scale(0.95); box-shadow: 0 0 0 0 rgba(106, 58, 143, 0); }
`;

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
`;

const ModalContainer = styled.div`
  width: 90%;
  max-width: 650px;
  background: linear-gradient(145deg, #1A1A2E, #16213E);
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);
  border: 1px solid #6A3A8F;
  animation: ${fadeIn} 0.5s ease-out;
`;

const ModalHeader = styled.div`
  background: linear-gradient(90deg, #88167a, #6A3A8F);
  padding: 20px;
  display: flex;
  align-items: center;
  gap: 15px;
  color: white;
  
  h2 {
    margin: 0;
    font-size: 1.5rem;
    font-weight: 600;
  }
`;

const ModalContent = styled.div`
  padding: 25px;
  color: #F8F9FA;
`;

const AILoaderContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 20px;
  margin-bottom: 30px;
`;

const AILoader = styled.div`
  position: relative;
  width: 120px;
  height: 120px;
`;

const AICore = styled.div`
  position: absolute;
  width: 100%;
  height: 100%;
  border-radius: 50%;
  background: radial-gradient(circle, #6A3A8F, #88167a);
  display: flex;
  justify-content: center;
  align-items: center;
  animation: ${pulse} 2s infinite;
`;

const AIRing = styled.div`
  position: absolute;
  width: 100%;
  height: 100%;
  border: 3px solid transparent;
  border-top-color: #00D1B2;
  border-radius: 50%;
  animation: ${spin} 1.5s linear infinite;
  
  &:nth-child(2) {
    width: 90%;
    height: 90%;
    border-top-color: #6A3A8F;
    animation-direction: reverse;
  }
  
  &:nth-child(3) {
    width: 80%;
    height: 80%;
    border-top-color: #00D1B2;
    animation-duration: 2s;
  }
`;

const AIStatus = styled.div`
  text-align: center;
  
  h3 {
    font-size: 1.2rem;
    margin-bottom: 10px;
    color: #00D1B2;
  }
  
  p {
    font-size: 0.9rem;
    opacity: 0.8;
    max-width: 400px;
    margin: 0 auto;
  }
`;

const ResultsContainer = styled.div`
  display: ${props => props.visible ? 'block' : 'none'};
  animation: ${fadeIn} 0.5s ease-out;
`;

const ResultsHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  padding-bottom: 10px;
  border-bottom: 1px solid #6A3A8F;
  
  h3 {
    color: #00D1B2;
    font-size: 1.2rem;
    margin: 0;
  }
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 15px;
  margin-bottom: 20px;
`;

const StatCard = styled.div`
  background: rgba(68, 10, 109, 0.2);
  border: 1px solid #6A3A8F;
  border-radius: 8px;
  padding: 15px;
  text-align: center;
  transition: transform 0.3s;
  
  &:hover {
    transform: translateY(-5px);
    background: rgba(68, 10, 109, 0.4);
  }
  
  h4 {
    font-size: 2rem;
    color: #00D1B2;
    margin-bottom: 5px;
  }
  
  p {
    font-size: 0.8rem;
    opacity: 0.8;
    margin: 0;
  }
`;

const DetailsSection = styled.div`
  background: rgba(26, 26, 46, 0.5);
  border-radius: 8px;
  padding: 15px;
  margin-top: 20px;
`;

const DetailItem = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 8px 0;
  border-bottom: 1px solid rgba(106, 58, 143, 0.3);
  
  &:last-child {
    border-bottom: none;
  }
  
  span:first-child {
    font-weight: 500;
    color: #00D1B2;
  }
  
  span:last-child {
    text-align: right;
  }
`;

const ErrorContainer = styled.div`
  display: ${props => props.visible ? 'flex' : 'none'};
  flex-direction: column;
  align-items: center;
  text-align: center;
  color: #FF3860;
  
  h3 {
    margin-bottom: 10px;
  }
`;

const ModalFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  padding: 15px 25px;
  background: rgba(26, 26, 46, 0.8);
  border-top: 1px solid #6A3A8F;
`;

const PrimaryButton = styled.button`
  padding: 10px 20px;
  border-radius: 6px;
  border: none;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s;
  background-color: #88167a;
  color: white;
  
  &:hover {
    background-color: #6A3A8F;
  }
`;

const TeacherAssignmentModal = ({ onClose, onAssign }) => {
  const [status, setStatus] = useState('loading');
  const [assignmentData, setAssignmentData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    const performAssignment = async () => {
      try {
        const response = await onAssign();
        setAssignmentData(response.data);
        setStatus('success');
      } catch (err) {
        setError(err.message || 'An unknown error occurred');
        setStatus('error');
      }
    };

    performAssignment();
  }, []);

  return (
    <ModalOverlay>
      <ModalContainer>
        <ModalHeader>
          <FontAwesomeIcon icon={faRobot} size="lg" />
          <h2 style={{color:"white"}}>AI Teacher Assignment</h2>
        </ModalHeader>
        
        <ModalContent>
          {status === 'loading' && (
            <AILoaderContainer>
              <AILoader>
                <AIRing />
                <AIRing />
                <AIRing />
                <AICore>
                  <FontAwesomeIcon icon={faBrain} size="2x" />
                </AICore>
              </AILoader>
              <AIStatus>
                <h3>Optimizing Teacher Assignments</h3>
                <p>
                  Our AI is analyzing teacher availability, timezones, qualifications, 
                  and scheduling constraints to create the optimal assignment plan.
                </p>
              </AIStatus>
            </AILoaderContainer>
          )}
          
          <ResultsContainer visible={status === 'success'}>
            <ResultsHeader>
              <h3>Assignment Results</h3>
              <FontAwesomeIcon icon={faCheckCircle} style={{ color: '#00D1B2', fontSize: '24px' }} />
            </ResultsHeader>
            
            <StatsGrid>
              <StatCard>
                <h4>{assignmentData?.data?.assigned || 0}</h4>
                <p>Successfully Assigned</p>
              </StatCard>
              <StatCard>
                <h4>{assignmentData?.data?.unassigned || 0}</h4>
                <p>Remaining Unassigned</p>
              </StatCard>
              <StatCard>
                <h4>{assignmentData?.data?.details?.length || 0}</h4>
                <p>Total Assignments Made</p>
              </StatCard>
              <StatCard>
                <h4>
                  {assignmentData?.data?.details?.reduce((acc, curr) => {
                    const teacherId = curr.teacherId;
                    if (!acc.includes(teacherId)) acc.push(teacherId);
                    return acc;
                  }, []).length || 0}
                </h4>
                <p>Teachers Utilized</p>
              </StatCard>
            </StatsGrid>
            
            <DetailsSection>
              <DetailItem>
                <span><FontAwesomeIcon icon={faChartLine} className="me-2" />Optimization Strategy</span>
                <span>Minimized overloads</span>
              </DetailItem>
              <DetailItem>
                <span><FontAwesomeIcon icon={faUserClock} className="me-2" />Timezone Matching</span>
                <span>Prioritized similar timezones</span>
              </DetailItem>
              <DetailItem>
                <span><FontAwesomeIcon icon={faLightbulb} className="me-2" />Recommendation</span>
                <span>
                  {assignmentData?.data?.unassigned > 0 
                    ? 'Consider adding more teachers or adjusting constraints' 
                    : 'All programs successfully assigned'}
                </span>
              </DetailItem>
            </DetailsSection>
          </ResultsContainer>
          
          <ErrorContainer visible={status === 'error'}>
            <FontAwesomeIcon icon={faExclamationTriangle} size="3x" style={{ marginBottom: '20px' }} />
            <h3>Assignment Failed</h3>
            <p>{error || 'An unexpected error occurred during the assignment process.'}</p>
          </ErrorContainer>
        </ModalContent>
        
        <ModalFooter>
          <PrimaryButton onClick={onClose}>
            {status === 'success' ? 'View Assignments' : 'Close'}
          </PrimaryButton>
        </ModalFooter>
      </ModalContainer>
    </ModalOverlay>
  );
};

export default TeacherAssignmentModal;